import Icon from "@/v2-ui/icon"
import cn from "@/v2-ui/utils/utils.cn"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import { createColumns } from "@/v2-backoffice-client/components/table"
import UptimeHistoryBar from "@/v2-backoffice-client/uptime/UptimeHistoryBar"
import UptimeLatencyBadge from "@/v2-backoffice-client/uptime/UptimeLatencyBadge"
import UptimeLatencySparkline from "@/v2-backoffice-client/uptime/UptimeLatencySparkline"
import UptimeMuteBadge from "@/v2-backoffice-client/uptime/mute/UptimeMuteBadge"
import {
  formatDurationSince,
  formatLatency,
  formatRelativeTime,
  formatUptimePercent,
  getCategoryLabel,
  getHostFromUrl,
  getPlatform,
  getPlatformLabel,
  getProviderName,
  getStatusVariant,
  getTargetStatusLabel,
  getUptimeSheetHref,
  targetSeverity,
  targetStatusKey,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import {
  UPTIME_PLATFORM_LABELS,
  UPTIME_PLATFORM_ORDER,
} from "@/v2-backoffice-client/uptime/uptime.constants"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

// Shared column registry powering both the Active Incidents table and
// the per-category tables. We expose every column the dashboard could
// possibly want; the rarely-read ones carry `defaultHidden` so they stay
// one toggle away in the columns panel instead of crowding every view.
//
// Splitting this from the consumers means the per-category view, the
// incident center, and any future "drift only" / "slow only" view all
// agree on:
//   - the same sort orders and grouping behaviour
//   - the same renderer for status / latency / drift / etc, so a row
//     looks identical no matter which list the operator is on
//   - the same column ids (so persisted layout from one table isn't
//     accidentally mismatched against a different schema in another)

// Default column widths -- expressed as defaults so the user's
// resize state still wins via the table's tableId persistence layer.
export const UPTIME_COLUMN_SIZING = {
  Status: 110,
  Name: 240,
  // "Platform" is the application-family bucket (Placepoint v1 /
  // Placepoint v2 / Propcloud / Newsecmaps) the row belongs to.
  // Hidden by default because the sidebar already groups by it; on
  // demand it lets an operator filter to a single platform when a
  // partner outage is in progress.
  Platform: 130,
  // "Category" is the monitor's bucket key (maplayer / app.release /
  // basemap / …) -- previously called "Source" before we split that
  // label off for the actual service provider name.
  Category: 130,
  // "Source" now means the provider company (Geodata, Kartverket,
  // Placepoint, …). Slightly wider than the host since provider
  // names are short but variable.
  Source: 140,
  Host: 200,
  Url: 260,
  Protocol: 110,
  AvgDurationMs: 100,
  P50: 80,
  P95: 80,
  P99: 80,
  Slow: 130,
  LatencySpark: 110,
  History: 200,
  Uptime90d: 90,
  Drift: 80,
  DownFor: 110,
  LastIncident: 130,
  LastError: 220,
  Muted: 90,
}

// Default column visibility shared by every uptime table. Keep the
// noisier columns hidden by default so the table stays scannable.
// Operators can surface them from the column-visibility menu.
//
// "Source" (provider) and "Host" are on by default -- the operator
// usually wants to know who runs the service (Geodata vs
// Miljødirektoratet vs Placepoint) and which exact host serves it.
// "Category" stays hidden by default because it tends to duplicate
// information the operator already has from the page they opened
// (category sidebar) and the breadcrumb in the view sheet.
export const UPTIME_DEFAULT_VISIBILITY = {
  Platform: false,
  Category: false,
  Protocol: false,
  P50: false,
  P99: false,
  LastError: false,
  Muted: false,
}

// "Slowness" of a target, expressed as the multiple of its 90-day
// baseline that the most recent 24h average represents (1.0 = exactly
// at baseline, 2.0 = twice as slow, 0.5 = twice as fast). Returns
// null when either value is missing or the baseline is zero -- the
// stored proc only flags `IsSlowOutlier` when both samples meet a
// minimum probe count, but plain ratio readouts shouldn't claim a
// number when the math is undefined.
function slownessRatio(row: UptimeTargetFragment): number | null {
  const a24 = row.AvgDurationMs24h
  const a90 = row.AvgDurationMs90d
  if (a24 == null || a90 == null || a90 <= 0) return null
  return a24 / a90
}

// Map (IsSlowOutlier flag, ratio) to a Tailwind text colour. Pulled
// out of the renderCell so the component body avoids a nested
// ternary (sonarqube doesn't love them) and the rule reads as one
// table.
function pickSlowTone(isSlow: boolean, ratio: number): string {
  if (isSlow) return "text-amber-500 font-medium"
  if (ratio >= 1.1) return "text-foreground"
  return "text-muted-foreground"
}

// Truncate long error strings so a single bad target can't blow up
// the row height; full text is in the title attribute.
function truncate(s: string, n: number): string {
  if (s.length <= n) return s
  return s.slice(0, n - 1) + "\u2026"
}

// `buildUptimeColumns` is the underlying registry factory. It exists
// so that future callers that need a different shape (e.g. a
// no-selection variant for an embedded table) can opt out of the
// row-checkbox column without monkeypatching the registry. Most
// call sites should reach for `UPTIME_TABLE_COLUMNS` instead -- the
// pre-built list with selection enabled, sized once at module scope
// so every table that mounts shares the same column instance and
// avoids paying for `createColumns` on every (re)render.
export function buildUptimeColumns(opts?: { enableSelection?: boolean }) {
  return createColumns<UptimeTargetFragment>({
    enableSelection: opts?.enableSelection ?? false,
    enableSorting: true,
    enableHiding: true,
    columns: {
      // Status column. The accessor returns a *logical* status key
      // ("down" / "slow" / "skipped" / "up") so the select filter
      // dropdown can match by string equality. We need this collapsed
      // vocabulary because IsSlowOutlier is a flag orthogonal to
      // LatestStatus -- a target can be "up but slow", and the operator
      // expects to filter that with the same status pill they see in
      // the row. Sorting falls back to severity (down > slow > skipped > up).
      Status: {
        id: "Status",
        label: "Status",
        enableGrouping: true,
        getValue: (row) => targetStatusKey(row),
        // Sort by severity rank rather than by the status string so
        // "desc" puts down first, then slow, then skipped, then up.
        // Falling back to alphabetical would land us in "down ←
        // skipped ← slow ← up" order, which is the wrong reading.
        sortingFn: (a, b) =>
          targetSeverity(a.original) - targetSeverity(b.original),
        filter: {
          type: "select",
          options: [
            { label: "Down", value: "down" },
            { label: "Slow", value: "slow" },
            { label: "Skipped", value: "skipped" },
            { label: "Up", value: "up" },
          ],
        },
        renderCell({ row }) {
          const t = row.original
          const key = targetStatusKey(t)
          // For "slow", surface the orange variant so the dot reads
          // as "warning" rather than the underlying "success" colour
          // of an up-but-slow target.
          const variant =
            key === "slow" ? "warning" : getStatusVariant(t.LatestStatus)
          return (
            <span className="uptime-incident-status-cell">
              <Dot variant={variant} />
              <span>{getTargetStatusLabel(key)}</span>
            </span>
          )
        },
        exportValue({ row }) {
          return getTargetStatusLabel(targetStatusKey(row.original))
        },
      },

      Name: {
        id: "Name",
        label: "Name",
        to: ({ row }) =>
          getUptimeSheetHref(row.original.Source, row.original.Identifier),
        getValue: (row) => row.Name ?? row.Identifier,
        renderCell({ row }) {
          const t = row.original
          const display = t.Name ?? t.Identifier
          return (
            <span className="uptime-incident-name-cell" title={t.Url}>
              <span className="truncate">{display}</span>
              {t.IsMuted && <UptimeMuteBadge target={t} className="ml-1.5" />}
            </span>
          )
        },
      },

      // "Platform" identifies which application family a target
      // belongs to (Placepoint v1, Placepoint v2, Propcloud,
      // Newsecmaps). Hidden by default since the sidebar already
      // groups by platform, but available for grouping / filtering
      // when an operator is investigating a partner-platform outage
      // and wants to scope every category-level table to it.
      Platform: {
        id: "Platform",
        label: "Platform",
        defaultHidden: true,
        enableGrouping: true,
        getValue: (row) => getPlatform(row),
        sortingFn: (a, b) => {
          const order = UPTIME_PLATFORM_ORDER as readonly string[]
          const ai = order.indexOf(getPlatform(a.original))
          const bi = order.indexOf(getPlatform(b.original))
          if (ai === -1 && bi === -1) return 0
          if (ai === -1) return 1
          if (bi === -1) return -1
          return ai - bi
        },
        filter: {
          type: "select",
          options: UPTIME_PLATFORM_ORDER.map((p) => ({
            label: UPTIME_PLATFORM_LABELS[p],
            value: p,
          })),
        },
        renderCell({ row }) {
          const platform = getPlatform(row.original)
          return (
            <span className="uptime-incident-source-cell">
              {getPlatformLabel(platform)}
            </span>
          )
        },
        exportValue({ row }) {
          return getPlatformLabel(getPlatform(row.original))
        },
      },

      // "Category" was previously called "Source" -- the column id was
      // renamed when we split the actual service-provider name out
      // into its own column. The accessor still reads `row.Source`
      // because the gql fragment hasn't moved; the *display* is the
      // human-friendly category label ("GIS Map Layers") rather than
      // the raw bucket key ("maplayer").
      Category: {
        id: "Category",
        label: "Category",
        defaultHidden: true,
        enableGrouping: true,
        getValue: (row) => row.Source,
        renderCell({ row }) {
          return (
            <span
              className="uptime-incident-source-cell"
              title={row.original.Source}
            >
              {getCategoryLabel(row.original.Source)}
            </span>
          )
        },
        exportValue({ row }) {
          return getCategoryLabel(row.original.Source)
        },
      },

      // "Source" now identifies the *provider company* behind the
      // service (Geodata, Kartverket, Placepoint, …). Derived
      // entirely from the URL host -- the gql fragment doesn't carry
      // a provider field, but a small directory of suffix → name
      // mappings covers the providers we publish layers from. Hosts
      // outside the directory render the bare host so the operator
      // still has something to recognise.
      Source: {
        id: "Source",
        label: "Source",
        enableGrouping: true,
        getValue: (row) => getProviderName(row),
        renderCell({ row }) {
          const t = row.original
          const provider = getProviderName(t)
          return (
            <span
              className="uptime-incident-source-cell"
              title={t.Url ?? provider}
            >
              {provider}
            </span>
          )
        },
        exportValue({ row }) {
          return getProviderName(row.original)
        },
      },

      // "Host" is the URL's hostname -- a secondary identifier that
      // disambiguates services from the same provider (e.g.
      // services1.geodataonline.no vs services3.geodataonline.no).
      // Hidden by default to keep the table scannable; available via
      // the column-visibility menu when an operator is hunting an
      // outage to a specific edge.
      Host: {
        id: "Host",
        label: "Host",
        defaultHidden: true,
        enableGrouping: true,
        getValue: (row) => getHostFromUrl(row.Url),
        renderCell({ row }) {
          const url = row.original.Url
          const host = getHostFromUrl(url)
          if (!host) {
            return <span className="text-muted-foreground/60">—</span>
          }
          if (!url) {
            return (
              <span className="uptime-incident-host-cell" title={host}>
                {host}
              </span>
            )
          }
          // The probe URL is the one thing an operator wants the moment a
          // row goes red -- to open it and see the upstream's own error.
          // Rendering it as a real anchor makes it openable, copyable
          // (right-click → copy link) and middle-clickable; the hover
          // title still carries the full URL for a quick read.
          return (
            <a
              className="uptime-incident-host-cell hover:underline"
              href={url}
              title={url}
              target="_blank"
              rel="noopener noreferrer"
              data-feature-code="UI_BackofficeUptime_Host_OpenProbeUrl"
              onClick={(e) => e.stopPropagation()}
            >
              {host}
            </a>
          )
        },
        exportValue({ row }) {
          return getHostFromUrl(row.original.Url) || null
        },
      },

      // The full probe URL. Hidden by default: on screen the Host cell
      // already links to it row by row. Its job is the Excel export --
      // enabling this column is what puts a per-target URL in the
      // exported sheet, which is the input tools/uptime/catalog_check.py
      // reads to ask each provider's catalog whether the URLs we have
      // are still the ones they advertise.
      Url: {
        id: "Url",
        label: "URL",
        defaultHidden: true,
        getValue: (row) => row.Url ?? "",
        renderCell({ row }) {
          const url = row.original.Url
          if (!url) {
            return <span className="text-muted-foreground/60">—</span>
          }
          return (
            <span className="uptime-incident-host-cell" title={url}>
              {truncate(url, 120)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Url || null
        },
      },

      Protocol: {
        id: "Protocol",
        label: "Protocol",
        defaultHidden: true,
        enableGrouping: true,
        getValue: (row) => row.Protocol,
      },

      // Numeric latency columns -- one per percentile + the 24h average.
      // The 24h average is the most operationally relevant ("how slow is
      // it RIGHT NOW") so it stays visible by default; the percentiles
      // (computed over the 30-day raw probe window; the field names keep
      // their legacy *90d suffix for API compatibility) are baseline
      // references the operator can surface when investigating
      // regressions.
      AvgDurationMs: {
        id: "AvgDurationMs",
        label: "Avg · 24h",
        getValue: (row) => row.AvgDurationMs24h ?? 0,
        renderCell({ row }) {
          return (
            <UptimeLatencyBadge
              currentMs={row.original.AvgDurationMs24h ?? null}
              baselineMs={
                row.original.P95DurationMs90d ??
                row.original.AvgDurationMs90d ??
                null
              }
              variant={row.original.IsSlowOutlier ? "critical" : undefined}
            />
          )
        },
        exportValue({ row }) {
          return row.original.AvgDurationMs24h ?? null
        },
        summaryValue({ row }) {
          return row.original.AvgDurationMs24h ?? null
        },
      },

      P50: {
        id: "P50",
        label: "p50 · 30d",
        defaultHidden: true,
        getValue: (row) => row.P50DurationMs90d ?? 0,
        renderCell({ row }) {
          const v = row.original.P50DurationMs90d
          return (
            <span className="tabular-nums text-xs text-muted-foreground">
              {v == null ? "—" : formatLatency(v)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.P50DurationMs90d ?? null
        },
        summaryValue({ row }) {
          return row.original.P50DurationMs90d ?? null
        },
      },

      P95: {
        id: "P95",
        label: "p95 · 30d",
        getValue: (row) => row.P95DurationMs90d ?? 0,
        renderCell({ row }) {
          const v = row.original.P95DurationMs90d
          return (
            <span className="tabular-nums text-xs">
              {v == null ? "—" : formatLatency(v)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.P95DurationMs90d ?? null
        },
        summaryValue({ row }) {
          return row.original.P95DurationMs90d ?? null
        },
      },

      P99: {
        id: "P99",
        label: "p99 · 30d",
        defaultHidden: true,
        getValue: (row) => row.P99DurationMs90d ?? 0,
        renderCell({ row }) {
          const v = row.original.P99DurationMs90d
          return (
            <span className="tabular-nums text-xs text-muted-foreground">
              {v == null ? "—" : formatLatency(v)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.P99DurationMs90d ?? null
        },
        summaryValue({ row }) {
          return row.original.P99DurationMs90d ?? null
        },
      },

      // "Slow" column: the heuristic boolean (`IsSlowOutlier`) plus a
      // human-readable readout of *how much* slower a target is than
      // its 90-day baseline. Filter is still boolean ("Slow / Normal")
      // so the operator can pick the slow tab without thinking about
      // ratios; sort uses the ratio so a shared sort puts the worst
      // regression at the top regardless of the underlying flag.
      Slow: {
        id: "Slow",
        label: "Slow",
        enableGrouping: true,
        // String accessor so the select filter below matches by
        // equality. Numeric ordering is handled by `sortingFn`.
        getValue: (row) => (row.IsSlowOutlier ? "slow" : "normal"),
        sortingFn: (a, b) => {
          // Treat missing data as "could not measure" -- sort below
          // every measured row so the operator's eyes land on real
          // regressions first when sorting desc.
          const ra = slownessRatio(a.original) ?? -Infinity
          const rb = slownessRatio(b.original) ?? -Infinity
          return ra - rb
        },
        filter: {
          type: "select",
          options: [
            { label: "Slow", value: "slow" },
            { label: "Normal", value: "normal" },
          ],
        },
        renderCell({ row }) {
          const t = row.original
          const ratio = slownessRatio(t)
          if (ratio == null) {
            return <span className="text-muted-foreground/60">—</span>
          }
          const a24 = t.AvgDurationMs24h ?? 0
          const a90 = t.AvgDurationMs90d ?? 0
          const deltaMs = Math.round(a24 - a90)
          const isSlow = t.IsSlowOutlier
          // Tone the cell so an operator can scan the column for
          // regressions: amber for the heuristic flag, plain for
          // mild slowdowns, faint for "around baseline / faster".
          const tone = pickSlowTone(isSlow, ratio)
          const sign = deltaMs >= 0 ? "+" : ""
          return (
            <span
              className={cn("tabular-nums text-xs", tone)}
              title={`${a24} ms now vs ${a90} ms baseline (90d)`}
            >
              {ratio.toFixed(2)}×
              <span className="text-muted-foreground/70 ml-1">
                ({sign}
                {deltaMs} ms)
              </span>
            </span>
          )
        },
        exportValue({ row }) {
          const ratio = slownessRatio(row.original)
          return ratio == null ? null : Number(ratio.toFixed(3))
        },
      },

      // Mini sparkline of the last 24h hourly latency. Visual sibling
      // to the response-time chart in the view sheet so an operator can
      // spot trends without opening every row.
      LatencySpark: {
        id: "LatencySpark",
        label: "Trend · 24h",
        enableSorting: false,
        renderCell({ row }) {
          return (
            <UptimeLatencySparkline
              history={row.original.LatencyHourlyHistory}
              baselineP95Ms={row.original.P95DurationMs90d}
              isDown={row.original.LatestStatus === "down"}
            />
          )
        },
      },

      History: {
        id: "History",
        label: "Last 30d",
        enableSorting: false,
        renderCell({ row }) {
          return (
            <UptimeHistoryBar
              className="uptime-incident-history-cell"
              history={row.original.DailyHistory}
              days={30}
            />
          )
        },
      },

      Uptime90d: {
        id: "Uptime90d",
        label: "Uptime · 90d",
        getValue: (row) => row.UptimePercent90d ?? 0,
        renderCell({ row }) {
          return (
            <span className="uptime-incident-uptime-cell tabular-nums">
              {formatUptimePercent(row.original.UptimePercent90d)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.UptimePercent90d ?? null
        },
        summaryValue({ row }) {
          return row.original.UptimePercent90d ?? null
        },
      },

      Drift: {
        id: "Drift",
        label: "Drift",
        enableGrouping: true,
        // Sort key prefers the most-recent drift timestamp across
        // both kinds, so a freshly-detected count drift sorts above
        // an older metadata drift on the same row. 0 still means
        // "no drift".
        getValue: (row) => {
          const hasMetadata = row.HasMetadataDrift
          const hasCount = row.HasCountDrift
          if (!hasMetadata && !hasCount) return 0
          const metaTs = row.DriftDetectedAt
            ? new Date(row.DriftDetectedAt).getTime()
            : 0
          const countTs = row.CountDriftDetectedAt
            ? new Date(row.CountDriftDetectedAt).getTime()
            : 0
          const max = Math.max(metaTs, countTs)
          return max > 0 ? max : 1
        },
        filter: {
          type: "select",
          options: [
            { label: "Drifting", value: "drift" },
            { label: "OK", value: "ok" },
          ],
        },
        renderCell({ row }) {
          const t = row.original
          const hasMetadata = t.HasMetadataDrift
          const hasCount = t.HasCountDrift
          if (!hasMetadata && !hasCount) {
            return <span className="text-muted-foreground/60">—</span>
          }
          // Build a single tooltip that calls out whichever kinds
          // are firing so the operator can see at a glance whether
          // it's a metadata change, a count drop, or both.
          const tooltipParts: string[] = []
          if (hasMetadata) {
            tooltipParts.push(
              t.DriftDetectedAt
                ? `Layer metadata changed since baseline (${formatRelativeTime(t.DriftDetectedAt)})`
                : "Layer metadata changed since baseline",
            )
          }
          if (hasCount) {
            const sev = t.CountDriftSeverity || "drift"
            tooltipParts.push(
              t.CountDriftDetectedAt
                ? `Feature count drifted (${sev}, ${formatRelativeTime(t.CountDriftDetectedAt)})`
                : `Feature count drifted (${sev})`,
            )
          }
          return (
            <span
              className="uptime-incident-drift-cell"
              title={tooltipParts.join(" · ")}
            >
              {hasMetadata && <Icon src="swap" size={11} />}
              {hasCount && <Icon src="tally" size={11} />}
              {hasCount && !hasMetadata ? "count" : "drift"}
            </span>
          )
        },
        exportValue({ row }) {
          const t = row.original
          if (t.HasMetadataDrift && t.HasCountDrift) return "drift+count"
          if (t.HasMetadataDrift) return "drift"
          if (t.HasCountDrift) return "count"
          return ""
        },
      },

      DownFor: {
        id: "DownFor",
        label: "Down for",
        getValue: (row) => {
          if (row.LatestStatus !== "down" || !row.CurrentStatusSince) return 0
          return Date.now() - new Date(row.CurrentStatusSince).getTime()
        },
        renderCell({ row }) {
          const t = row.original
          if (t.LatestStatus !== "down" || !t.CurrentStatusSince) {
            return <span className="text-muted-foreground/60">—</span>
          }
          // `CurrentStatusSince` is the oldest retained check that still
          // has today's status, so for a target that has never once
          // answered it reports when *monitoring* started, not when the
          // upstream broke -- three unrelated providers all reading
          // "down for 1 month 22 days" is the monitor's own start date.
          // Where the window holds no successful check at all, say
          // "at least" rather than implying we watched it go down.
          const neverUp =
            t.TotalChecks90d > 0 && t.DownChecks90d === t.TotalChecks90d
          return (
            <span
              className="text-destructive tabular-nums"
              title={
                neverUp
                  ? "Never seen up since monitoring began — the outage may predate the first check"
                  : undefined
              }
            >
              {neverUp && "≥ "}
              {formatDurationSince(t.CurrentStatusSince)}
            </span>
          )
        },
      },

      LastIncident: {
        id: "LastIncident",
        label: "Last incident",
        getValue: (row) => {
          if (!row.LastErrorAt) return 0
          return new Date(row.LastErrorAt).getTime()
        },
        renderCell({ row }) {
          if (!row.original.LastErrorAt) {
            return <span className="text-muted-foreground/60">—</span>
          }
          return (
            <span className="tabular-nums">
              {formatRelativeTime(row.original.LastErrorAt)}
            </span>
          )
        },
      },

      LastError: {
        id: "LastError",
        label: "Last error",
        defaultHidden: true,
        getValue: (row) => row.LastError ?? "",
        renderCell({ row }) {
          const e = row.original.LastError
          if (!e) return null
          const status = row.original.LastErrorHttpStatus
          return (
            <span
              className="uptime-incident-error-cell text-destructive"
              title={e}
            >
              {status != null && <strong>HTTP {status}</strong>}
              {status != null && " · "}
              {truncate(e, 120)}
            </span>
          )
        },
      },

      Muted: {
        id: "Muted",
        label: "Muted",
        defaultHidden: true,
        enableGrouping: true,
        getValue: (row) => (row.IsMuted ? 1 : 0),
        filter: {
          type: "select",
          options: [
            { label: "Muted", value: "muted" },
            { label: "Active", value: "active" },
          ],
        },
        renderCell({ row }) {
          if (!row.original.IsMuted) {
            return <span className="text-muted-foreground/60">—</span>
          }
          return <UptimeMuteBadge target={row.original} />
        },
        exportValue({ row }) {
          return row.original.IsMuted ? "muted" : ""
        },
      },
    },
  })
}

// Module-level instance of the column registry. Built once on first
// import; every table that consumes it (incident center, per-category
// page) reads the same column array reference, so the underlying
// react-table instance can short-circuit prop comparisons instead
// of treating "fresh useMemo result with identical content" as a
// new schema. Selection is enabled because every page that uses the
// shared registry today needs the bulk-mute toolbar; the rare caller
// that wants a no-selection variant can fall back to
// `buildUptimeColumns({ enableSelection: false })`.
export const UPTIME_TABLE_COLUMNS = buildUptimeColumns({
  enableSelection: true,
})
